import { IS_PROD } from "@/lib/global_imports";
import { buildFingerprint } from "@/lib/fingerprint";
import { getEffectiveUiLanguage } from "@/l11n/l11n";
import { doLog, APP_ID } from "@/lib/globals";
import { TaaliaEvent } from "./event_types";
import {
  localStorageGet,
  localStorageSet,
  STORAGE_KEYS,
  clearLocalStorage,
  clearSessionStorage,
} from "@/lib/storage";
import { getFirstBrowserLanguage } from "./web";
import { requestQueue } from "./request_queue";
import LogRocket from "logrocket";

const FORCE_PROD = false;
const WORKER_PROD = "https://api.taalia.com/v1";

const ENDPOINTS = {
  chat: "chat",
  event: "event",
  user: "user",
  userFeedback: "user/feedback",
  l11n: "l11n",
  logout: "logout",
  activity: "assessment",
  activityToken: "assessment/token",
  activityScorePoll: "assessment/scorepoll",
  installQrCode: "util/qr-tag",
};

// MARK: postChat
export async function postChat(message, queryParams, waitForResponse) {
  try {
    if (doLog()) {
      console.log("Posting message:", message);
      console.log("Query params:", queryParams);
    }

    // Get the LCID
    // const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    if (!lcid) {
      console.warn("LCID unavailable in postMessage().");
      return;
    }

    // Construct the URL with query parameters
    const url = buildApiUrl(ENDPOINTS.chat, queryParams);

    // Create the body of the request
    const body = JSON.stringify(message ? message : {});

    // Create the headers
    const headers = {
      "Content-Type": "application/json",
      "Installation-Id": "web",
      "App-Id": APP_ID,
      lcid,
    };

    // Add the fingerprint to the headers conditionally
    if (message.ixid?.messageId === "email") {
      if (doLog()) console.log("Adding fingerprint to headers");
      const fp = await buildFingerprint();
      headers["Client-Fingerprint"] = fp;
    }

    // Add the turnstile token to the headers if it is available
    const turnstileTokenSentInSession = localStorageGet(STORAGE_KEYS.turnstileTokenSent);
    if (turnstileTokenSentInSession) {
      if (doLog()) console.log("Turnstile token already sent in session.");
    } else {
      const turnstileToken = localStorageGet(STORAGE_KEYS.turnstileToken);
      if (turnstileToken) {
        if (doLog()) console.log("Adding tt header");
        headers["tt"] = turnstileToken;
        localStorageSet(STORAGE_KEYS.turnstileTokenSent, true);
      } else {
        // if (doLog()) console.warn("tt not available.");
      }
    }

    // Create the request object
    const request = {
      method: "POST",
      body,
      headers,
      credentials: "include", // Ensure cookies are included in the request
    };

    if (doLog()) console.log(`postMessage() request`, { ...request });

    // TEMPORARY: log the message being posted
    postLog(`postChat(): ${message.ixid?.messageId ?? "unknown"}`, message);
    // END TEMPORARY

    // Log Queue Stats
    // postQueueStats();

    if (waitForResponse) {
      // Send the request to the server using the queue
      const response = await requestQueue.add({
        url,
        options: request,
      });

      if (response.status >= 400) {
        // Server errors
        const body = await response.text();
        return [
          {
            isError: true,
            code: response.status,
            utterance: `${body} (${response.status})`,
            senderId: "api",
            ixid: {
              messageId: "error",
              scriptId: "common",
            },
          },
        ];
      }

      // Convert the response to JSON
      const data = await response.json();
      if (doLog()) console.log("Chat Server Response:", data);

      // Response headers
      pullCountryCodeFromResponseHeaders(response);

      // Return the response
      return data;
    } else {
      // Fire and forget - still use queue but don't wait for response
      requestQueue.add({
        url,
        options: request,
      });
    }
  } catch (error) {
    const msg = `Error in postMessage() ${error.message}`;
    console.error(msg, error);
    postError(msg, error);
    return [
      {
        isError: true,
        utterance: error.message ?? "Error generating response from the server.",
        senderId: "api",
        ixid: {
          messageId: "error",
          scriptId: "common",
        },
      },
    ];
  }
}

// MARK: postError
export function postError(message, error) {
  try {
    if (doLog()) console.log(`postError: ${message}`);
    postEvent(TaaliaEvent.log, {
      level: "error",
      message,
      error,
    });

    // Log to LogRocket
    // https://docs.logrocket.com/reference/capture-exception
    LogRocket.captureException(error, {
      // tags: {
      //   // additional data to be grouped as "tags"
      //   subscription: "Pro",
      // },
      extra: {
        message,
      },
    });
  } catch (error) {
    console.error(`Error in postError() ${error.message}`, error);
  }
}

// MARK: postLog
export async function postLog(message, data = {}) {
  try {
    if (doLog()) console.log(`postLog: ${message}`);

    // Log to console here so LogRocket gets it
    console.log(message, data);

    // Log to the server
    const eventName = TaaliaEvent.log;
    const url = buildApiUrl(ENDPOINTS.event);

    // Get the LCID
    // const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    if (!lcid) {
      console.warn("LCID unavailable in postLog().", { message, data });
      return;
    }

    // Create the body of the request
    const bodyObj = {
      eventName,
      level: "log",
      message,
      data,
    };
    const body = JSON.stringify(bodyObj);

    // Create the headers
    const headers = {
      "Content-Type": "application/json",
      "Installation-Id": "web",
      "App-Id": APP_ID,
      lcid,
      "Event-Name": eventName,
    };

    const request = {
      method: "POST",
      body,
      headers,
      credentials: "include", // Ensure cookies are included in the request
    };

    if (doLog()) console.log(`postLog() request`, { ...request });

    // Log Queue Stats
    // postQueueStats();

    // Send the request to the server using queue
    const response = await requestQueue.add({
      url,
      options: request,
    });

    if (!response) {
      console.error("No response from postLog()", {
        message,
        data: JSON.stringify(data),
      });
      return;
    }

    // Check for errors
    if (response.status >= 400) {
      const msg = `Error in postLog(): ${response.status}`;
      console.error(msg);
    }

    pullCountryCodeFromResponseHeaders(response);

    // Log the response
    // if (doLog()) {
    //   const body = await response.text();
    //   console.log(`postLog() response:`, body);
    // }
  } catch (error) {
    console.error(`Error in postLog() ${error.message}`, error);
  }
}

// MARK: postEvent
export async function postEvent(eventName, eventParameters = {}) {
  if (doLog()) console.log(`postEvent()`, { eventName, eventParameters });

  // Log to LogRocket
  // https://docs.logrocket.com/reference/track
  LogRocket.track(eventName, {
    eventParameters: JSON.stringify(eventParameters),
  });

  // Log to the server
  const url = buildApiUrl(ENDPOINTS.event);

  // Get the LCID
  // const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
  const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
  if (!lcid) {
    console.warn("LCID unavailable in postEvent().", { eventName, eventParameters });
    return;
  }

  // Create the body of the request
  const bodyObj = {
    eventName,
    eventParameters,
  };
  const body = JSON.stringify(bodyObj);

  // Create the headers
  const headers = {
    "Content-Type": "application/json",
    "Installation-Id": "web",
    "App-Id": APP_ID,
    lcid,
    "Event-Name": eventName,
  };

  const request = {
    method: "POST",
    body,
    headers,
    credentials: "include",
  };

  if (doLog()) console.log(`postEvent() request`, { ...request });

  // Log Queue Stats
  // postQueueStats();

  // Send the request to the server using queue
  const response = await requestQueue.add({
    url,
    options: request,
  });

  // Check for errors
  if (response.status >= 400) {
    console.error(
      `Error in postEvent(): ${response?.status ?? "unknown status"}`,
      response
    );
    return;
  }

  pullCountryCodeFromResponseHeaders(response);

  // Log the response
  // if (doLog()) {
  //   const body = await response.text();
  //   console.log(`postEvent() response:`, body);
  // }
}

// MARK: Post Feedback
export async function postFeedback(feedback) {
  try {
    // Build the URL
    const url = buildApiUrl(ENDPOINTS.userFeedback);

    // Create the headers
    const headers = {
      "Installation-Id": "web",
      "App-Id": "53@5sd+7f7",
    };

    // Create the body
    const body = JSON.stringify(feedback);

    // Create the request
    const request = {
      method: "POST",
      headers,
      body,
    };

    // Log Queue Stats
    // postQueueStats();

    // Send the request using queue
    const response = await requestQueue.add({
      url,
      options: request,
    });

    // Check for errors
    if (response.status >= 400) {
      const msg = `Error in postFeedback(): ${response.status}`;
      console.error(msg);
      postError(msg);
    }
  } catch (error) {
    const msg = "Error in postFeedback()";
    console.error(msg, error);
    return null;
  }
}

// MARK: postQStats
// function postQueueStats() {
//   try {
//     if (isProdHost) return;
//     if (doLog()) console.log("postQueueStats()");

//     // Get the LCID
//     const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
//     if (!lcid) {
//       console.warn("LCID unavailable in postQueueStats().");
//       return;
//     }

//     const eventName = TaaliaEvent.queueStats;
//     const stats = requestQueue.getQueueStats();
//     const bodyObj = {
//       eventName,
//       eventParameters: stats,
//       message: "Queue Stats",
//     };
//     const body = JSON.stringify(bodyObj);

//     // Create the headers
//     const headers = {
//       "Content-Type": "application/json",
//       "Installation-Id": "web",
//       "App-Id": APP_ID,
//       lcid,
//       "Event-Name": eventName,
//     };

//     const request = {
//       method: "POST",
//       headers,
//       body,
//       credentials: "include",
//     };

//     if (doLog()) console.log("postQueueStats() request:", { ...request });

//     const url = buildApiUrl(ENDPOINTS.event);

//     // Bypass the request queue and send the request directly without awaiting
//     fetch(url, request).catch((error) => {
//       console.error(`Error in postQueueStats() ${error.message}`, error);
//     });
//   } catch (error) {
//     console.error(`Error in postQueueStats() ${error.message}`, error);
//   }
// }

// MARK: getAssessmentPdfUrl
export async function getAssessmentPdfUrl(lcid, assessmentId) {
  const url = buildApiUrl(
    `${ENDPOINTS.activityToken}?assessmentId=${assessmentId}&format=pdf`
  );

  const headers = {
    "Installation-Id": "web",
    "App-Id": "53@5sd+7f7",
  };
  const request = {
    method: "GET",
    headers,
    credentials: "include",
  };

  // Log Queue Stats
  // postQueueStats();

  // Send the request using queue
  const response = await requestQueue.add({
    url,
    options: request,
  });

  if (response.status >= 400) {
    const msg = `Error in getAssessmentPdfUrl(): ${response.status}`;
    console.error(msg);
    postError(msg);
  }
  const data = await response.json();
  if (doLog()) console.log("getAssessmentPdfUrl() response:", data);
  return data.url;
}

// MARK: L11n Pack
export async function getLocalizationPack(packId) {
  try {
    if (doLog()) console.log("getLocalizationPack()", { packId });

    // Get the LCID
    // const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    if (!lcid) {
      console.warn("LCID unavailable in getLocalizationPack().");
      return;
    }

    // Get the effective UI language
    const lang = getFirstBrowserLanguage();

    // Build the URL
    const url = buildApiUrl(`${ENDPOINTS.l11n}/${packId}`);

    // Create the headers
    const headers = {
      "Installation-Id": "web",
      "App-Id": "53@5sd+7f7",
      "device-lang": lang,
      lcid,
    };

    // Create the request
    const request = {
      method: "GET",
      headers,
      credentials: "include",
    };

    // Log Queue Stats
    // postQueueStats();

    // Send the request using queue
    const response = await requestQueue.add({
      url,
      options: request,
    });

    // Check for errors
    if (response.status >= 400) {
      const msg = `Error in getLocalizationPack(): ${response.status}`;
      console.error(msg);
      postError(msg);
    }
    const data = await response.json();

    pullCountryCodeFromResponseHeaders(response);

    if (doLog()) console.log("getLocalizationPack() response:", data);

    return data;
  } catch (error) {
    const msg = "Error in getLocalizationPack()";
    console.error(msg, error);
    return null;
  }
}

// MARK: L11n Manifest
export async function getLocalizationForManifest(manifest) {
  try {
    // Get the LCID
    // const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    if (!lcid) {
      console.warn("LCID unavailable in postEvent().");
      return;
    }

    // Get the effective UI language
    const lang = getEffectiveUiLanguage();

    // Build the URL
    const url = buildApiUrl(ENDPOINTS.l11n);

    // Create the headers
    const headers = {
      "Installation-Id": "web",
      "App-Id": "53@5sd+7f7",
      "device-lang": lang,
      lcid,
    };

    // Create the body
    const body = JSON.stringify(manifest);

    // Create the request
    const request = {
      method: "POST",
      headers,
      credentials: "include",
      body,
    };

    // Log Queue Stats
    // postQueueStats();

    // Send the request using queue
    const response = await requestQueue.add({
      url,
      options: request,
    });

    // Check for errors
    if (response.status >= 400) {
      const msg = `Error in getLocalizationForManifest(): ${response.status}`;
      console.error(msg);
      postError(msg);
      return null;
    }
    const data = await response.json();

    pullCountryCodeFromResponseHeaders(response);

    if (doLog()) console.log("getLocalizationForManifest() response:", data);

    return data;
  } catch (error) {
    const msg = "Error in getLocalizationForManifest()";
    console.error(msg, error);
    return null;
  }
}

// MARK: Logout
export async function logout() {
  try {
    if (doLog()) console.log("logout()");
    await postEvent(TaaliaEvent.logout);

    // Build the URL
    const queryParams = Object.fromEntries(new URLSearchParams(window.location.search));
    console.log("queryParams", queryParams);
    const url = buildApiUrl(ENDPOINTS.logout, queryParams, true);

    // Create the request
    const request = {
      method: "GET",
      credentials: "include",
    };

    // Log Queue Stats
    // postQueueStats();

    // Send the request using queue
    const response = await requestQueue.add({
      url,
      options: request,
    });

    if (doLog()) console.log("Logout response:", JSON.stringify(response, null, 2));

    // Check for errors
    if (response.status >= 400) {
      const msg = `Error in logout(): ${response.status}`;
      console.error(msg);
      postError(msg);
    } else {
      // Clear session storage
      if (doLog()) console.log("Clearing session storage");
      clearSessionStorage();

      // Clear local storage
      if (doLog()) console.log("Clearing local storage");
      clearLocalStorage();

      // Clear the request queue
      requestQueue.clearQueue();

      // Redirect to the new URL
      const redirectUrl = await response.text();
      if (doLog()) console.log("Redirect URL:", redirectUrl);
      window.location.href = redirectUrl;
    }
  } catch (error) {
    console.error("Error in logout()", error);
  }
}

// MARK: pollForScore
export async function getActivityScore() {
  try {
    if (doLog()) console.log("getActivityScore()");

    // Get the LCID
    // const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    if (!lcid) {
      console.warn("LCID unavailable in postMessage().");
      return;
    }

    // Construct the URL with query parameters
    const url = buildApiUrl(ENDPOINTS.activityScorePoll);

    // Create the headers
    const headers = {
      "Content-Type": "application/json",
      "Installation-Id": "web",
      "App-Id": APP_ID,
      lcid,
    };

    // Create the request object
    const request = {
      method: "GET",
      headers,
      credentials: "include",
    };
    if (doLog()) console.log(`getActivityScore() request`, { ...request });

    // Log Queue Stats
    // postQueueStats();

    // Send the request to the server using queue
    const response = await requestQueue.add({
      url,
      options: request,
    });

    if (response.status >= 400) {
      // Server errors
      const body = await response.text();
      return [
        {
          isError: true,
          code: response.status,
          utterance: `${body} (${response.status})`,
          senderId: "api",
          ixid: {
            messageId: "error",
            scriptId: "common",
          },
        },
      ];
    }

    // Convert the response to JSON
    const data = await response.json();
    if (doLog()) console.log("getActivityScore() response:", data);

    // Return the response
    return data;
  } catch (error) {
    const msg = `Error in getActivityScore() ${error.message}`;
    console.error(msg, error);
    postError(msg, error);
    return -1;
  }
}

// MARK: QR Code
export async function getInstallQrCode() {
  try {
    if (doLog()) console.log("getInstallQrCode()");

    // Get the LCID
    // const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    const lcid = localStorageGet(STORAGE_KEYS.lcidKey);
    if (!lcid) {
      console.warn("LCID unavailable in postMessage().");
      return;
    }

    // Construct the URL with query parameters
    const url = buildApiUrl(ENDPOINTS.installQrCode);

    // Create the headers
    const headers = {
      "Content-Type": "application/json",
      "Installation-Id": "web",
      "App-Id": APP_ID,
      lcid,
    };

    // Create the request object
    const request = {
      method: "GET",
      headers,
      credentials: "include",
    };
    if (doLog()) console.log(`getActivityScore() request`, { ...request });

    // Log Queue Stats
    // postQueueStats();

    // Send the request to the server using queue
    const response = await requestQueue.add({
      url,
      options: request,
    });

    if (response.status >= 400) {
      // Server errors
      const body = await response.text();
      return [
        {
          isError: true,
          code: response.status,
          utterance: `${body} (${response.status})`,
          senderId: "api",
          ixid: {
            messageId: "error",
            scriptId: "common",
          },
        },
      ];
    }

    // Convert the response to JSON
    const data = await response.text();
    if (doLog()) console.log("getInstallQrCode() response:", data);

    // Return the response
    return data;
  } catch (error) {
    const msg = `Error in getInstallQrCode() ${error.message}`;
    console.error(msg, error);
    postError(msg, error);
    return -1;
  }
}

// MARK: INTERNALS

function getApiUrlDev() {
  return `http://${window.location.hostname}:8911/v1`;
}

function buildApiUrl(endpoint, queryParams, omitVersion = false) {
  // Determine the worker URL based on the environment
  let apiUrlBase = FORCE_PROD || IS_PROD ? WORKER_PROD : getApiUrlDev();
  apiUrlBase = `${apiUrlBase}/${endpoint}`;

  if (omitVersion) {
    apiUrlBase = apiUrlBase.replace("/v1", "");
  }

  if (doLog()) console.log("API URL:", apiUrlBase);

  // Construct the URL
  const url = new URL(apiUrlBase);

  // Add query parameters to the URL
  if (queryParams) {
    Object.keys(queryParams).forEach((key) =>
      url.searchParams.append(key, queryParams[key])
    );
  }

  // Return the constructed URL
  return url;
}

function pullCountryCodeFromResponseHeaders(response) {
  // console.log("response.headers", response.headers);
  const countryCode = response.headers.get("taalia-cc");
  // console.log("pullCountryCodeFromResponseHeaders found country code:", countryCode);
  if (countryCode) {
    localStorageSet(STORAGE_KEYS.countryCode, countryCode.toUpperCase());
  }
}
